import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renewSubscription, type AdminSubscription } from '../../../api/subscriptions.api';

type Props = {
  open: boolean;
  subscription: AdminSubscription | null;
  onClose: () => void;
  onRenewed: () => void;
};

export default function RenewSubscriptionModal({ open, subscription, onClose, onRenewed }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'days' | 'months'>('months');
  const [value, setValue] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode('months');
    setValue('1');
    setError('');
    setSaving(false);
  }, [open, subscription?.id]);

  if (!open || !subscription) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = Number(value);
    if (!Number.isFinite(num) || num < 1) {
      setError(t('subscriptions.renew.value_invalid'));
      return;
    }
    setSaving(true);
    setError('');
    const result = await renewSubscription(subscription.id, mode === 'months' ? { months: num } : { days: num });
    setSaving(false);
    if (result.ok === false) {
      setError(result.message);
      return;
    }
    onRenewed();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('subscriptions.renew.title')}</h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {subscription.tenant?.name ?? subscription.tenant_id}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          {error ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{error}</div>
          ) : null}

          <p className="text-sm text-gray-600">{t('subscriptions.renew.hint')}</p>

          <div className="grid grid-cols-2 gap-2">
            {([
              { key: 'months' as const, label: t('subscriptions.renew.mode_months') },
              { key: 'days' as const, label: t('subscriptions.renew.mode_days') },
            ]).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setMode(opt.key)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold cursor-pointer ${
                  mode === opt.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">
              {mode === 'months' ? t('subscriptions.renew.months') : t('subscriptions.renew.days')}
            </label>
            <input
              type="number"
              min={1}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              required
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer">
              {t('subscriptions.renew.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 cursor-pointer"
            >
              {saving ? t('subscriptions.renew.saving') : t('subscriptions.renew.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
